import React, { useEffect, useState } from 'react';
import { Trophy, Users, Gift, Star, Zap, Target, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { Button } from '@nimbitlabs/ui';
import { OnboardingLoadingSkeleton } from '@/components/OnboardingLoadingSkeleton';
import { useServicesStore } from '@/stores/servicesStore';
import { useTeamsStore } from '@/stores/teamsStore';
import { useEnvironmentsStore } from '@/stores/environmentsStore';
import { useMonitoringConfigsStore } from '@/stores/monitoringConfigsStore';
import { useUsers } from '@nimbitlabs/features/users';
import { useAuth } from '@nimbitlabs/auth';
import { useAchievementStore } from '@/stores/achievementStore';
import { useReferralStats } from '@nimbitlabs/features/account';
import { cn } from '@nimbitlabs/ui';

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  unlocked: boolean;
  unlockedAt?: Date;
  reward?: string;
}

export const AchievementBadges: React.FC = () => {
  const { user } = useAuth();
  const currentAccountId = user?.accountId;
  const { referralCount } = useReferralStats(currentAccountId);
  const isMinimized = useAchievementStore(state => state.isMinimized);
  const setMinimized = useAchievementStore(state => state.setMinimized);
  
  // Read from stores - DO NOT fetch, GamificationPanel handles fetching
  const services = useServicesStore(state => state.services);
  const teams = useTeamsStore(state => state.teams);
  const environments = useEnvironmentsStore(state => state.environments);
  const monitoringConfigs = useMonitoringConfigsStore(state => state.configs);
  
  const servicesLoading = useServicesStore(state => state.loading);
  const teamsLoading = useTeamsStore(state => state.loading);
  const environmentsLoading = useEnvironmentsStore(state => state.loading);
  
  const { data: users = [], isLoading: usersLoading } = useUsers();

  // Define achievements
  const achievements: Achievement[] = [
    {
      id: 'onboarding_complete',
      title: 'Setup Champion',
      description: 'Complete all onboarding steps',
      icon: Trophy,
      unlocked: 
        environments.length > 0 &&
        teams.length > 0 &&
        services.length > 0 &&
        monitoringConfigs.length > 0,
      reward: '🎉 Full platform access unlocked',
    },
    {
      id: 'five_referrals',
      title: 'Growth Partner',
      description: 'Refer 5 activated accounts',
      icon: Gift,
      unlocked: referralCount >= 5,
      reward: referralCount >= 5 ? '🎁 Receive 10% discount' : 'Receive 10% discount',
    },
  ];

  const unlockedAchievements = achievements.filter(a => a.unlocked);
  const totalAchievements = achievements.length;

  const handleToggleMinimize = () => {
    setMinimized(!isMinimized);
  };

  // Don't show if no achievements unlocked
  if (unlockedAchievements.length === 0) {
    return null;
  }

  return (
    <div className="mx-3 md:mx-6 mt-2 md:mt-3 mb-2">
      <div 
        className="flex items-center justify-between py-1.5 md:py-2 px-2 md:px-3 rounded-lg hover:bg-muted/30 cursor-pointer transition-colors group"
        onClick={handleToggleMinimize}
      >
        <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
          <Star className="h-3.5 w-3.5 md:h-4 md:w-4 text-yellow-500 shrink-0" />
          <h3 className="text-xs md:text-sm font-semibold text-foreground truncate">
            Achievements
          </h3>
          <span className="text-xs text-muted-foreground shrink-0">
            {unlockedAchievements.length}/{totalAchievements}
          </span>
          <div className="hidden sm:block flex-1 h-px bg-border ml-3" />
        </div>
        
        <div className="ml-2 md:ml-3 shrink-0">
          {isMinimized ? (
            <ChevronDown className="h-3.5 w-3.5 md:h-4 md:w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
          ) : (
            <ChevronUp className="h-3.5 w-3.5 md:h-4 md:w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
          )}
        </div>
      </div>

      {!isMinimized && (
        <div className="flex flex-col md:flex-row md:flex-wrap gap-2 md:gap-3 mt-2">
          {achievements.map((achievement) => {
          const Icon = achievement.icon;
          
          return (
            <div
              key={achievement.id}
              className={cn(
                "group relative flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg border-2 transition-all duration-300",
                achievement.unlocked
                  ? "bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-yellow-500/10 border-yellow-500/30 shadow-md hover:shadow-lg md:"
                  : "bg-muted/30 border-muted opacity-60"
              )}
            >
              {/* Icon */}
              <div className={cn(
                "flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full transition-all shrink-0",
                achievement.unlocked
                  ? "bg-yellow-500/20 text-yellow-500 group-"
                  : "bg-muted text-muted-foreground"
              )}>
                <Icon className={cn(
                  "h-4 w-4 md:h-5 md:w-5",
                  achievement.unlocked && "animate-pulse"
                )} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 md:gap-2">
                  <h4 className={cn(
                    "text-xs md:text-sm font-semibold truncate",
                    achievement.unlocked ? "text-foreground" : "text-muted-foreground"
                  )}>
                    {achievement.title}
                  </h4>
                  {achievement.unlocked && (
                    <div className="flex items-center gap-1 shrink-0">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-yellow-500 animate-pulse" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1 md:line-clamp-none">
                  {achievement.description}
                </p>
                {achievement.reward && (
                  <p className={cn(
                    "text-xs mt-0.5 md:mt-1 font-medium line-clamp-1",
                    achievement.unlocked ? "text-yellow-600 dark:text-yellow-400" : "text-muted-foreground"
                  )}>
                    {achievement.reward}
                  </p>
                )}
              </div>

              {/* Locked overlay */}
              {!achievement.unlocked && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-lg">
                  <div className="flex items-center justify-center w-10 h-10 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-yellow-500/30 to-orange-500/30 border-2 border-yellow-500/60 shadow-xl shadow-yellow-500/30">
                    <Lock className="h-5 w-5 md:h-7 md:w-7 text-yellow-600 dark:text-yellow-400" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
        </div>
      )}
    </div>
  );
};
